import React from 'react';
import ReactDom from 'react-dom';
import PropTypes from 'prop-types';
import validate from './validations';
import Component from './component';
import unpick from '../utilities/unpick';
import { unsafeSend } from '../../utilities/messages';
import {
  cardTypeFromNumber,
  extendCardList,
  checkForExtendedCardNumberValidation,
  payform,
} from '../utilities/payform';
import { filterFramesByFieldId, filterFramesByFieldType } from '../../utilities/filters';

const isEnterPress = (event) => {
  const code = event.keyCode || event.which;
  return event.type === 'keypress' && code === 13;
};

export default class Controller extends React.Component {
  constructor() {
    super();
    this.onUpdate = this.onUpdate.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { addCardBrands, type, cardType } = this.props;
    if (addCardBrands && prevProps.addCardBrands !== addCardBrands) {
      extendCardList(addCardBrands);
    }
    if (type === 'card-security-code') {
      if (prevProps.cardType !== cardType) {
        this.resetState(this.props.fieldState.value);
      }
    }
  }

  componentDidMount() {
    this.hackForIOSFocus();
    checkForExtendedCardNumberValidation(this.props.validations);
    const { fieldState, defaultValue } = this.props;
    if (typeof defaultValue !== 'undefined') {
      this.resetState(defaultValue);
    } else {
      this.resetState('');
    }
  }

  componentWillUnmount() {
    document.body.removeEventListener('touchstart', this.eventForTouch);
  }

  onUpdate(event) {
    if (['checkbox', 'radio'].includes(this.props.type)) {
      return this.resetState(event.target.checked, event.type);
    } else if (this.props.type !== 'textarea' && isEnterPress(event)) {
      unsafeSend(window.parent, 'enterPress', {
        name: this.props.name,
        formId: this.props.formId
      });
    } else {
      return this.resetState(event.target.value, event.type);
    }
  }

  getErrorMessages(value) {
    return validate(value, this.props.validations, this.props);
  }

  hackForIOSFocus = () => {
    const userAgent = window.navigator.userAgent;
    const iOS = /iPad/i.test(userAgent) || /iPhone/i.test(userAgent);
    const webkit = /WebKit/i.test(userAgent);
    const iOSSafari = iOS && webkit && !/CriOS/i.test(userAgent);
    if (iOS && webkit && iOSSafari) {
      document.body.addEventListener('click', this.hackForIOSTouch);
    }
  };

  hackForIOSTouch = (e) => {
    document.activeElement.value += ' ';
    document.activeElement.value = document.activeElement.value.slice(0, -1);
  };

  resetState(value = '', eventType = '') {
    const element = ReactDom.findDOMNode(this); // eslint-disable-line react/no-find-dom-node

    let adjustedValue = value;
    if (['checkbox', 'radio'].includes(this.props.type)) {
      adjustedValue = value ? this.props.valueWhenChecked : '';
    }

    const errorMessages = this.getErrorMessages(adjustedValue);
    const state = Object.assign({}, this.props.fieldState, {
      value: adjustedValue,
      errorMessages: errorMessages,
      isValid: errorMessages.length === 0,
      isFocused: document.activeElement === element && eventType !== 'blur' && !!(element.type || element.href),
      isTouched: !this.props.fieldState.isTouched && eventType === 'blur' ? true : this.props.fieldState.isTouched,
    });

    if (adjustedValue !== this.props.fieldState.value) {
      state.isDirty = true;
    }
    if (adjustedValue.length === 0) {
      state.isEmpty = true;
    } else {
      state.isEmpty = false;
    }
    if (this.props.type === 'card-number') {
      state.cardType = cardTypeFromNumber(adjustedValue);
      state.last4 = null;
      state.bin = null;
      if (state.isValid && state.value.length >= 12) {
        state.last4 = state.value.replace(/\s+/g, '').slice(-4);
      }
      if (state.isValid && state.value.length >= 12) {
        state.bin = state.value.replace(/\s+/g, '').slice(0, 6);
      }

      if (this.props.cvcDependency) {
        const { cvcDependency } = this.props;
        const cardTypeListener = filterFramesByFieldId(cvcDependency, filterFramesByFieldType('card-security-code'));
        cardTypeListener.forEach((field) => {
          unsafeSend(field, 'changeCardType', { cardType: state.cardType, cards: payform.cards });
        });
      }
    }
    if (this.props.type === 'ssn') {
      state.last4 = null;
      if (state.isValid && state.value.length > 4) {
        state.last4 = state.value.replace(/\s+/g, '').slice(-4);
      }
    }
    this.props.onUpdate(state);
  }

  render() {
    let props = unpick(this.props, [
      'name',
      'validations',
      'onUpdate',
      'defaultValue',
      'addCardBrands',
      'cvcDependency',
      'formId',
    ]);
    if (this.props.type !== 'card-expiration-date') {
      props = unpick(props, ['yearLength']);
    }
    if (this.props.type === 'card-security-code') {
      props = unpick(props, ['cardType']);
    }
    if (this.props.showCardIcon) {
      props.cardType = props.fieldState.cardType || '';
    }

    return <Component {...props} onUpdate={this.onUpdate} />;
  }
}

const validationPropType = PropTypes.oneOfType([PropTypes.bool, PropTypes.object]);

Controller.propTypes = {
  type: PropTypes.string.isRequired,
  valueWhenChecked: PropTypes.string,
  validations: PropTypes.shape({
    validCardSecurityCode: validationPropType,
    validCardNumber: validationPropType,
    validCardExpirationDate: validationPropType,
    required: validationPropType,
  }),
  onUpdate: PropTypes.func.isRequired,
};

Controller.defaultProps = {
  validations: undefined,
};
